import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, ReplaySubject, retry, Subject, Subscription, take, takeUntil } from 'rxjs';

import {
	ApolloCacheManager,
	apolloErrorContains,
	nonRetryableErrorSubStrings,
	SubscriptionManager,
	SubscriptionManagerConfigCreate
} from '@shure/cloud/shared/apollo';
import { CloudRoom } from '@shure/cloud/shared/models/devices';
import { monitorLoginState, OktaInterfaceService } from '@shure/cloud/shared/okta/data-access';
import { ILogger } from '@shure/shared/angular/utils/logging';

import { CloudRoomsApiService } from '../api/cloud-rooms-api.service';

import {
	CloudRoomFragment,
	CloudRoomSubscriptionGQL,
	CloudRoomsQueryGQL,
	CloudRoomsSubscriptionGQL
} from './graphql/generated/cloud-sys-api';
import { mapCloudRoomFromSysApi } from './mappers/map-cloud-room';

const PAGE_SIZE = 1000;

@Injectable()
export class SysApiCloudRoomsApiService implements CloudRoomsApiService {
	public readonly cloudRoomsLoadingComplete$ = new BehaviorSubject<boolean>(false);
	public readonly cloudRoomAdded$ = new Subject<CloudRoom>();
	public readonly cloudRoomRemoved$ = new Subject<string>();
	public readonly cloudRoomSnapshot$ = new Subject<CloudRoom>();

	private readonly cloudRoomIds = new Set<string>();
	private destroy$ = new Subject<void>();
	private isInitialized = false;
	private readonly logger: ILogger;
	private readonly subscriptionReady$ = new ReplaySubject<void>(1);

	private readonly cloudRoomSubscriptionManager = new SubscriptionManager({
		subscriptionType: 'cloud-rooms',
		create: (config): Subscription => this.createCloudRoomSubscription(config),
		retryWaitMs: 10_000,
		maxRetryAttempts: 3
	});

	constructor(
		logger: ILogger,
		oktaService: OktaInterfaceService,
		private readonly cloudRoomsQueryGQL: CloudRoomsQueryGQL,
		private readonly cloudRoomsSubscriptionGQL: CloudRoomsSubscriptionGQL,
		private readonly cloudRoomSubscriptionGQL: CloudRoomSubscriptionGQL,
		private readonly apolloCacheManager: ApolloCacheManager
	) {
		this.logger = logger.createScopedLogger('SysApiCloudRoomsApiService');

		monitorLoginState(oktaService, {
			onLogIn: this.initService.bind(this),
			onLogOut: this.suspendService.bind(this)
		});
	}

	public getCachedCloudRooms$(): Observable<CloudRoom[]> {
		const apolloCache = this.apolloCacheManager.getApolloCache('sysApi');
		if (!apolloCache) {
			this.logger.error('getCachedCloudRooms', 'Apollo cache "sysApi" not found');
			return of([]);
		}

		const cachedCloudRooms: CloudRoom[] = [];
		for (const id of this.cloudRoomIds) {
			const cachedCloudRoom = apolloCache.readFragment<CloudRoomFragment>({
				// eslint-disable-next-line @typescript-eslint/naming-convention
				id: apolloCache.identify({ __typename: 'CloudRoom', id }),
				fragment: CloudRoomFragment
			});

			const cloudRoom = mapCloudRoomFromSysApi(cachedCloudRoom);
			if (cloudRoom) {
				cachedCloudRooms.push(cloudRoom);
			}
		}

		return of(cachedCloudRooms);
	}

	private initService(): void {
		if (this.isInitialized) {
			return;
		}

		this.isInitialized = true;
		this.destroy$ = new Subject();

		this.cloudRoomsLoadingComplete$.next(false);
		this.subscribeToCloudRoomEvents();
		this.subscriptionReady$.pipe(take(1), takeUntil(this.destroy$)).subscribe(() => {
			this.queryAllCloudRooms(null);
		});
	}

	private suspendService(): void {
		this.isInitialized = false;
		this.cloudRoomsLoadingComplete$.next(true);
		this.destroy$.next();
		this.destroy$.complete();
		this.cloudRoomSubscriptionManager.deregisterAll();
	}

	private subscribeToCloudRoomEvents(): void {
		this.cloudRoomsSubscriptionGQL
			.subscribe({ variables: {}, fetchPolicy: 'network-only' })
			.pipe(retry({ count: 2, delay: 10_000 }), takeUntil(this.destroy$))
			.subscribe({
				next: (result) => {
					const event = result.data?.rooms;

					if (!event) {
						return;
					}

					if ('created' in event) {
						const cloudRoom = mapCloudRoomFromSysApi(event.created);
						if (cloudRoom) {
							this.cloudRoomAdded$.next(cloudRoom);
							this.cloudRoomSubscriptionManager.register([cloudRoom.id]);
						}
					} else if ('deleted' in event) {
						this.cloudRoomSubscriptionManager.deregister(event.deleted);
						this.cloudRoomRemoved$.next(event.deleted);
					} else if ('ready' in event) {
						this.subscriptionReady$.next();
					}
				},
				error: (error: unknown) => {
					this.logger.error('subscribeToCloudRoomEvents', 'cloud rooms subscription error', error);
				}
			});
	}

	private queryAllCloudRooms(afterCursor: string | null): void {
		this.cloudRoomsQueryGQL
			.fetch({
				variables: { paginationArgs: { first: PAGE_SIZE, after: afterCursor } },
				fetchPolicy: 'network-only'
			})
			.pipe(retry({ count: 2, delay: 5_000 }), take(1), takeUntil(this.destroy$))
			.subscribe({
				next: (result) => {
					const connection = result.data?.roomsConnection;

					if (!connection) {
						return;
					}

					for (const edge of connection.edges) {
						if (edge.node) {
							const cloudRoom = mapCloudRoomFromSysApi(edge.node);
							if (cloudRoom) {
								this.cloudRoomAdded$.next(cloudRoom);
								this.cloudRoomSubscriptionManager.register([cloudRoom.id]);
							}
						}
					}

					if (connection.pageInfo.hasNextPage && connection.pageInfo.endCursor) {
						this.queryAllCloudRooms(connection.pageInfo.endCursor);
					} else {
						this.cloudRoomsLoadingComplete$.next(true);
					}
				},
				error: (error: unknown) => {
					this.logger.error('queryAllCloudRooms', 'failed to query cloud rooms', error);
					this.cloudRoomsLoadingComplete$.next(true);
				}
			});
	}

	private createCloudRoomSubscription({
		id,
		retryCallback,
		eventObserver
	}: SubscriptionManagerConfigCreate): Subscription {
		return this.cloudRoomSubscriptionGQL
			.subscribe({
				variables: {
					id
				},
				errorPolicy: 'ignore',
				fetchPolicy: 'network-only'
			})
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (result) => {
					const roomData = result.data?.nodeChanges;

					if (roomData && '__typename' in roomData && roomData.__typename === 'CloudRoom') {
						this.handleCloudRoomSnapshot(roomData as CloudRoomFragment);
					}

					if (eventObserver) {
						eventObserver.eventReceived();
					}
				},
				complete: () => {
					this.logger.error(
						'cloudRoomSubscriptionGQL',
						'subscription completed. Will retry.',
						JSON.stringify({ nodeId: id })
					);
					retryCallback();
				},
				error: (error: unknown) => {
					if (apolloErrorContains(error, nonRetryableErrorSubStrings)) {
						return;
					}
					this.logger.error(
						'cloudRoomSubscriptionGQL',
						'Encountered retryable error',
						JSON.stringify({ id, error }, null, 3)
					);
					retryCallback();
				}
			});
	}

	private handleCloudRoomSnapshot(cloudRoomFragment: CloudRoomFragment): void {
		const updatedRoom = mapCloudRoomFromSysApi(cloudRoomFragment);

		if (!updatedRoom) {
			return;
		}
		this.cloudRoomSnapshot$.next(updatedRoom);
	}
}
